import { createSelector } from 'reselect'

import { facetByIdSelector } from '~/app/store/facet'
import { refinementsHasRefSelector } from '~/app/store/refinement'
import { categoryByIdSelector } from '~/app/store/filterCategories'
import { stringSort } from '~/app/utils/sort'
import { FILTER_TYPE } from '~/shared/constants'

/**
 * Returns the first selected child category for the given root
 */
export const selectedL1SelectorFactory = () =>
  createSelector(
    categoryByIdSelector,
    refinementsHasRefSelector,
    (_state: State.GlobalState, rootId: string) => rootId,
    (getCategory, getSelected, rootId): { selectedCategoryId: string | null } => {
      const root = getCategory(rootId)

      if (!root || !root.children || root.children.length <= 0) {
        return {
          selectedCategoryId: null,
        }
      }

      const selectedId = root.children.find((c: string) => !!getSelected(c))

      return {
        selectedCategoryId: selectedId || null,
      }
    },
  )

export const selectedChildrenSelectorFactory = () =>
  createSelector(
    categoryByIdSelector,
    refinementsHasRefSelector,
    (_state: State.GlobalState, rootId: string) => rootId,
    (getCategory, getSelected, rootId): SelectedValues => {
      const root = getCategory(rootId)

      if (!root || !root.children || root.children.length <= 0) {
        return {}
      }

      return root.children.reduce((accum, c) => {
        const selected = !!getSelected(c)

        return {
          ...accum,
          [c]: selected,
        }
      }, {} as SelectedValues)
    },
  )

/**
 * Returns a selector to generate sorted filter options for a given category filter
 */
export const categoryOptionsSelectorFactory = (rootId: string, shouldAddDisabled: boolean) =>
  createSelector([categoryByIdSelector, facetByIdSelector, refinementsHasRefSelector], (getCategory, getFacet, getSelected) => {
    const root = getCategory(rootId)
    const selectedValues: SelectedValues = {}

    if (!root || !root.children || root.children.length <= 0) {
      return {
        options: [],
        selectedValues,
        visible: false,
      }
    }

    let enabled = false // the filter will be rendered if there is at least one
    let options = root.children.map((c: string) => {
      const category = getCategory(c)
      const facet = getFacet(c)
      const disabled = !facet || facet.count <= 0

      if (!disabled) {
        enabled = true
      }

      const selected = !!getSelected(c)

      if (selected) {
        selectedValues[c] = selected
      }

      return {
        disabled,
        selected,
        value: c,
        title: category.title,
        facetCount: 0,
      }
    }) as RenderableCategoryFilterOption[]

    // sort, rank alphabetically, removing unavailable options
    options = options
      .filter((o) => shouldAddDisabled || !o.disabled)
      .sort(stringSort('title'))
      .map((c: RenderableCategoryFilterOption, i: number) => ({
        ...c,
        rank: i,
      }))

    return {
      options,
      selectedValues,
      visible: enabled,
    }
  })

export const normalizeFilterType = (type?: string): FILTER_TYPE => (type ?? FILTER_TYPE.UNKNOWN).toLowerCase() as FILTER_TYPE

export const isSubstrateColorFilter = (type?: string): boolean => normalizeFilterType(type) === FILTER_TYPE.SUBSTRATE_COLOR
